import { STORAGE_ACCESS_TOKEN, STORAGE_REFRESH_TOKEN, STORAGE_USER } from "./storageKeys";
import { peopleAuthUrl } from "./peopleAuth";
import { redirectToShellHome } from "./redirectToShell";
import { refreshWithRetry } from "./sessionRenewal";

// Cookie names — mirror packages/weco-django-platform/.../auth_constants.py
// (WECO_ACCESS_COOKIE, WECO_REFRESH_COOKIE). Refresh is HttpOnly so JS can't
// read it; only the access cookie is reachable here. See ADR-0003.
const ACCESS_COOKIE_NAME = "weco-access-token";
// Parent cookie scope used by GH-570 cross-subdomain auth (ADR-0003).
const ACCESS_COOKIE_PARENT_DOMAIN = ".we-co.io";

function parseJwt(token: string): { exp?: number } | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

function isTokenExpiringSoon(token: string | null, bufferSeconds = 120): boolean {
  if (!token) return true;
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return payload.exp - now < bufferSeconds;
}

function readAccessFromLocalStorage(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(STORAGE_ACCESS_TOKEN);
}

/**
 * Delete the legacy `weco-refresh-token` localStorage copy.
 *
 * ADR-0003 shipped the HttpOnly refresh cookie alongside the localStorage copy
 * and named dropping localStorage as the follow-up; this is that drop. The
 * refresh token is the one credential worth stealing -- it is long-lived and
 * mints access tokens for as long as it lives -- and in localStorage any XSS
 * on any *.we-co.io SPA can read it. In the HttpOnly cookie it cannot.
 *
 * Nothing reads this key any more, so the purge is about not leaving a usable
 * credential sitting in storage on every machine that has ever signed in.
 */
export function purgeLegacyRefreshToken(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STORAGE_REFRESH_TOKEN);
}

function readAccessFromCookie(): string | null {
  // GH-570 / ADR-0003: cross-subdomain SPAs whose localStorage is empty fall
  // back to the Domain=.we-co.io access cookie. JS can read it because the
  // access cookie is HttpOnly=false (refresh stays HttpOnly=true).
  if (typeof document === "undefined") return null;
  const cookies = document.cookie || "";
  const target = `${ACCESS_COOKIE_NAME}=`;
  for (const part of cookies.split(";")) {
    const trimmed = part.trim();
    if (trimmed.startsWith(target)) {
      const raw = trimmed.slice(target.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw || null;
      }
    }
  }
  return null;
}

function readAccess(): string | null {
  const ls = readAccessFromLocalStorage();
  if (ls) return ls;
  return readAccessFromCookie();
}

/**
 * Wipe the `weco-access-token` cookie from the DOM at BOTH the parent
 * `.we-co.io` scope (where GH-570 sets it) AND the current host. The
 * host-level wipe is defensive: prior buggy flows may have set the cookie
 * host-scoped, and a parent-scoped delete won't clear a host-scoped cookie.
 *
 * Refresh token stays HttpOnly so JS cannot delete it from here; the backend
 * Set-Cookie on /logout/ handles that (which is why the logout fetch must
 * use `credentials: "include"` — see GH-776 D3/D4/D7).
 */
export function clearAccessCookieFromDom(): void {
  if (typeof document === "undefined") return;
  const expired = "Thu, 01 Jan 1970 00:00:00 GMT";
  // Parent-domain scope (Domain=.we-co.io)
  document.cookie = `${ACCESS_COOKIE_NAME}=; Path=/; Domain=${ACCESS_COOKIE_PARENT_DOMAIN}; Expires=${expired}; Max-Age=0; SameSite=Lax`;
  // Host-only scope (no Domain attribute)
  document.cookie = `${ACCESS_COOKIE_NAME}=; Path=/; Expires=${expired}; Max-Age=0; SameSite=Lax`;
}

export function clearSuiteAuthFromStorage(): void {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(STORAGE_USER);
    localStorage.removeItem(STORAGE_ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_REFRESH_TOKEN);
  }
  clearAccessCookieFromDom();
}

// When the shell registers a handler (via setRefreshHandler), all refresh
// calls go through it so the shell store's single-flight dedup is the sole
// authority. Without this, tokenStorage and the shell store each have their
// own in-flight variable — both fire concurrently with the same refresh token,
// the backend's token-reuse detector revokes the whole family, and the user is
// logged out mid-session (observed as ~45-minute logout with 15-min access tokens).
let refreshHandlerOverride: (() => Promise<boolean>) | null = null;

export function setRefreshHandler(fn: () => Promise<boolean>): void {
  refreshHandlerOverride = fn;
}

let refreshInFlight: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  if (refreshHandlerOverride) {
    return refreshHandlerOverride();
  }
  // The HttpOnly refresh cookie is the only source now. `credentials:
  // 'include'` makes it ride along cross-origin too, which is required when
  // the SPA origin differs from people-backend.
  //
  // Deliberately NOT falling back to a localStorage copy, even one that is
  // still there from before this change: the backend prefers a body token
  // over the cookie, so a stale copy would be preferred over a good cookie,
  // be rejected, and trip the token-reuse detector -- which revokes the whole
  // family and signs the user out. Cookie-only is the one order that cannot
  // do that.
  const init: RequestInit = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: "{}",
  };

  // GH-1913: only a definitive rejection (400/401/403) clears auth. A 429,
  // 5xx or network error is retried inside refreshWithRetry and this promise
  // stays pending until the session is renewed or definitively rejected.
  const ok = await refreshWithRetry(
    () => fetch(peopleAuthUrl("refresh/"), init),
    async (res) => {
      const data = (await res.json()) as { access?: string; refresh?: string };
      if (data.access && typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_ACCESS_TOKEN, data.access);
      }
      // `data.refresh` is deliberately dropped on the floor. The response also
      // carries a rotated Set-Cookie for it, which is the copy we keep.
    },
  );
  if (!ok) clearSuiteAuthFromStorage();
  return ok;
}

/**
 * Forces an immediate token refresh regardless of current token expiry state.
 * Reuses the same in-flight dedup as getValidAccessTokenFromStorage so
 * concurrent callers await the same refresh rather than issuing parallel requests.
 * Used by SuiteApiClient to recover from a 403 "Access token has expired" response.
 */
export async function forceRefreshToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight;
  }
  refreshInFlight = doRefresh();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Returns a valid access token, refreshing via refresh token when near expiry.
 * Reads from localStorage first, then falls back to the cross-subdomain cookie.
 */
export async function getValidAccessTokenFromStorage(): Promise<string | null> {
  ensurePollerInstalled();

  let access = readAccess();
  if (isTokenExpiringSoon(access, 120)) {
    if (refreshInFlight) {
      const ok = await refreshInFlight;
      access = readAccess();
      return ok ? access : null;
    }
    refreshInFlight = doRefresh();
    try {
      const ok = await refreshInFlight;
      access = readAccess();
      return ok ? access : null;
    } finally {
      refreshInFlight = null;
    }
  }
  return access;
}

// ---------------------------------------------------------------------------
// AC14: cross-subdomain session invalidation poller (GH-570)
//
// When the user logs out on origin A, the auth cookie is cleared at the parent
// domain (.we-co.io). Origin B's tab needs to notice within 5s. We can't use
// BroadcastChannel cross-origin, so we poll: if we previously had auth and now
// don't (and a refresh attempt fails), clear local state + redirect to login.
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 5000;
let pollerInstalled = false;
let pollerHandle: ReturnType<typeof setInterval> | null = null;
let lastAuthState: "authed" | "unauthed" = "unauthed";

function hasAnyAuth(): boolean {
  if (readAccessFromLocalStorage()) return true;
  return readAccessFromCookie() !== null;
}

async function checkAuthStillValid(): Promise<void> {
  const isAuthed = hasAnyAuth();
  if (lastAuthState === "authed" && !isAuthed) {
    // Lost auth between polls — try one refresh (handles "access expired but
    // refresh cookie still valid"). Reuse refreshInFlight if a refresh is
    // already underway from a normal API call.
    let ok: boolean;
    if (refreshInFlight) {
      ok = await refreshInFlight;
    } else {
      refreshInFlight = doRefresh();
      try {
        ok = await refreshInFlight;
      } finally {
        refreshInFlight = null;
      }
    }
    if (!ok) {
      lastAuthState = "unauthed";
      clearSuiteAuthFromStorage();
      const returnTo = typeof window !== "undefined" ? window.location.href : undefined;
      redirectToShellHome(returnTo);
      return;
    }
  }
  lastAuthState = isAuthed ? "authed" : "unauthed";
}

export function installAuthInvalidationPoller(intervalMs: number = POLL_INTERVAL_MS): () => void {
  if (pollerInstalled) return () => {};
  if (typeof window === "undefined") return () => {};
  pollerInstalled = true;
  lastAuthState = hasAnyAuth() ? "authed" : "unauthed";
  pollerHandle = setInterval(() => {
    void checkAuthStillValid();
  }, intervalMs);
  return () => {
    if (pollerHandle !== null) {
      clearInterval(pollerHandle);
      pollerHandle = null;
    }
    pollerInstalled = false;
  };
}

function ensurePollerInstalled(): void {
  if (pollerInstalled || typeof window === "undefined") return;
  installAuthInvalidationPoller();
}

if (typeof window !== "undefined") {
  purgeLegacyRefreshToken();
  installAuthInvalidationPoller();
}
